import isEqual from 'lodash.isequal';
import { DependencyList, useEffect, useRef } from 'react';

type EffectCallback = (
  controller: AbortController
) => void | (() => void | undefined);

function processDependencies(dependencies: DependencyList): DependencyList {
  return dependencies.map(dependency =>
    typeof dependency === 'function'
      ? `${dependency.name || 'unknown'} function`
      : dependency == null
      ? dependency
      : JSON.parse(JSON.stringify(dependency))
  );
}

/**
 * This hook exposes an `AbortController` to the effect's function. This
 * controller should be passed to data fetchers within the hook. If the effect
 * is disposed (dependencies change or the component unmounts) the controller
 * will abort, causing the data fetchers to cancel.
 *
 * An optional `condition` boolean can be passed as a third argument. If the
 * condition is false, the effect will not run.
 *
 * @example
 * ```
 * // Request will cancel if zip code changes while request is running
 * useEffectWithController((controller) => {
 *   axios(`https://www.example.com?zip={zip}`, {
 *     signal: controller.signal
 *   }).then((res) => {
 *     setState(res.data);
 *   });
 * }, [zip);
 * ```
 */
export default function useEffectWithController(
  effect: EffectCallback,
  dependencies: DependencyList
) {
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  useEffect(() => {
    const controller = new AbortController();

    const destructor = effect(controller);

    return () => {
      controller.abort({
        message: 'Dependencies changed or component unmounted',
        newDependencies: processDependencies(dependenciesRef.current),
        previousDependencies: processDependencies(dependencies),
        changes: dependenciesRef.current.map((dependency, index) =>
          dependency === dependencies[index]
            ? 'same'
            : isEqual(dependency, dependencies[index])
            ? 'changed reference'
            : 'different'
        )
      });

      return typeof destructor === 'function' ? destructor() : undefined;
    };
  }, dependencies); // eslint-disable-line react-hooks/exhaustive-deps
}
